import { Box, Stack } from '@mui/material';
import { AssessmentMethodType } from '@schooly/api';
import { FormTextField } from '@schooly/components/form-text-field';
import { getControllerErrorText } from '@schooly/utils/get-controller-error-text';
import * as React from 'react';
import { FC, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { AssessmentForm } from '../AssessmentCreateContent';
import { SCORE_OUT_OF_MAX } from '../useAssessmentCreateModal';
import { DeleteMethodButton } from './AssessmentsCreateModalSettings.styled';
import { AssessmentsCreateModalSettingsMethodType } from './AssessmentsCreateModalSettingsMethodType';

export interface AssessmentsScoreControlProps {
  autoFocus?: boolean;
  onMethodChange?: (methodType?: AssessmentMethodType) => void;
  onDelete?: () => void;
  index: number;
}

export const AssessmentsScoreControl: FC<AssessmentsScoreControlProps> = ({
  autoFocus,
  onMethodChange,
  onDelete,
  index,
}) => {
  const { formatMessage } = useIntl();
  const name = `methods.${index}` as const;
  const { control, watch } = useFormContext<AssessmentForm>();
  const method = watch(name);

  const validateScoreOutOf = useCallback(
    (value: { score_out_of?: number | null }) => {
      if (value.score_out_of === null || value.score_out_of === undefined) {
        return formatMessage({ id: 'input-ErrorRequired' });
      }

      return true;
    },
    [formatMessage],
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={{ validate: validateScoreOutOf }}
      render={({ field, fieldState }) => {
        const error = fieldState.error;
        const hasError = !!error;
        const errorMessage = getControllerErrorText(error, undefined, formatMessage);

        return (
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Box sx={{ flex: '0 0 49%' }}>
              <AssessmentsCreateModalSettingsMethodType
                methodType={field.value.method_type ?? undefined}
                onChange={onMethodChange}
              />
            </Box>
            <Box width="100%">
              <FormTextField
                label={formatMessage({ id: 'assessments-ScorePlaceholder' })}
                inputProps={{
                  min: 0,
                  max: SCORE_OUT_OF_MAX,
                  value: field.value.score_out_of ?? '',
                }}
                value={field.value.score_out_of}
                onChange={(e) => {
                  const scoreOutOf = e.target.value;

                  let score_out_of;

                  if (scoreOutOf == null || scoreOutOf === '') {
                    score_out_of = null;
                  } else if (Number.isNaN(parseInt(scoreOutOf))) {
                    // NaN should not update value
                    return;
                  } else {
                    score_out_of = Number.parseInt(scoreOutOf);

                    if (score_out_of < 0) {
                      score_out_of = 0;
                    } else if (score_out_of > SCORE_OUT_OF_MAX) {
                      score_out_of = SCORE_OUT_OF_MAX;
                    }
                  }
                  field.onChange({ ...method, score_out_of });
                }}
                error={hasError}
                helperText={errorMessage}
                autoFocus={autoFocus}
                fullWidth
                required
              />
            </Box>
            <Box>
              <DeleteMethodButton onClick={onDelete} />
            </Box>
          </Stack>
        );
      }}
    />
  );
};
